import { ref, computed, watch, readonly } from 'vue'

export type Theme = 'light' | 'dark' | 'system'

const THEME_STORAGE_KEY = 'qrmb-theme-preference'

// Reactive theme preference state
const themePreference = ref<Theme>('system')
const systemPrefersDark = ref(false)

// Computed actual theme
const currentTheme = computed(() => {
  if (themePreference.value === 'system') {
    return systemPrefersDark.value ? 'dark' : 'light'
  }
  return themePreference.value
})

const isDark = computed(() => currentTheme.value === 'dark')

export function useTheme() {
  // Initialize theme from localStorage or default to system
  function initializeTheme() {
    const stored = localStorage.getItem(THEME_STORAGE_KEY) as Theme | null
    if (stored && ['light', 'dark', 'system'].includes(stored)) {
      themePreference.value = stored
    }

    // Set up system theme detection
    updateSystemPreference()

    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = (e: MediaQueryListEvent) => {
      systemPrefersDark.value = e.matches
    }
    mediaQuery.addEventListener('change', handler)

    return () => mediaQuery.removeEventListener('change', handler)
  }

  function updateSystemPreference() {
    systemPrefersDark.value = window.matchMedia('(prefers-color-scheme: dark)').matches
  }

  function setTheme(theme: Theme) {
    themePreference.value = theme
    localStorage.setItem(THEME_STORAGE_KEY, theme)
  }

  function toggleTheme() {
    setTheme(isDark.value ? 'light' : 'dark')
  }

  // Apply theme to document
  function applyTheme() {
    const root = document.documentElement

    if (isDark.value) {
      root.classList.add('dark')
      root.style.colorScheme = 'dark'
    } else {
      root.classList.remove('dark')
      root.style.colorScheme = 'light'
    }

    // Optimized theme color update - single RAF call
    requestAnimationFrame(() => {
      const fallback = isDark.value ? '#121212' : '#ffffff'
      const computedBg = getComputedStyle(root).getPropertyValue('--bg-primary').trim()
      const normalizedColor = normalizeThemeColor(computedBg, fallback)
      updateThemeColorMeta(normalizedColor || fallback)
    })
  }

  function updateThemeColorMeta(color: string) {
    let metaThemeColor = document.querySelector('meta[name="theme-color"]')

    if (!metaThemeColor) {
      metaThemeColor = document.createElement('meta')
      metaThemeColor.setAttribute('name', 'theme-color')
      document.head.appendChild(metaThemeColor)
    }

    metaThemeColor.setAttribute('content', color)
  }

  function normalizeThemeColor(value: string, fallback: string) {
    if (!value) return fallback

    const lower = value.toLowerCase()

    if (lower.startsWith('#')) {
      if (lower.length === 4) {
        const r = lower[1]
        const g = lower[2]
        const b = lower[3]
        return `#${r}${r}${g}${g}${b}${b}`
      }

      if (lower.length === 9) {
        return lower.slice(0, 7)
      }

      return lower
    }

    const rgbMatch = lower.match(/rgba?\(([^)]+)\)/)
    if (rgbMatch) {
      const parts = rgbMatch[1].split(',').map((part) => parseFloat(part.trim()))
      const [r, g, b, alpha] = parts

      if ([r, g, b].every((channel) => Number.isFinite(channel))) {
        if (typeof alpha === 'number' && alpha < 1) {
          return fallback
        }

        const toHex = (channel: number) => channel.toString(16).padStart(2, '0')
        return `#${toHex(Math.round(r))}${toHex(Math.round(g))}${toHex(Math.round(b))}`
      }
    }

    return fallback
  }

  // Watch for theme changes and apply them
  watch(currentTheme, applyTheme, { immediate: true })

  // Store theme preference changes
  watch(themePreference, (newTheme) => {
    localStorage.setItem(THEME_STORAGE_KEY, newTheme)
  })

  return {
    themePreference: readonly(themePreference),
    currentTheme: readonly(currentTheme),
    isDark: readonly(isDark),
    setTheme,
    toggleTheme,
    initializeTheme,
    applyTheme
  }
}